import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import fr from 'facet-rest';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import IconButton from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/IconButton';
import HeightIcon from '@material-ui/icons/Height';
import { pickImplementation } from 'Helpers/Affordances';
import useUserContext from 'Context/UserContext';
import { useHandleVisitTab } from 'Components/UI/CustomLayout/CustomLayoutContext';
import ColumnTabs from './ColumnTabs';
import ColumnContent from './ColumnContent';

const TABS_HEIGHT = 40;

const styles = theme => ({
  root: {
    position: 'relative',
    flexGrow: 1,
  },
  tabContainer: {
    height: '100%',
  },
  tabs: {
    height: TABS_HEIGHT,
  },
  content: {},
  dragIcon: {
    transform: 'rotate(90deg) translateY(50%)',
    position: 'absolute',
    top: theme.spacing(0.25),
    padding: theme.spacing(0.75),
    left: 0,
    cursor: 'grab !important',

    '&:active': {
      cursor: 'grabbing !important',
    },
  },
});

const Column = ({
  className,
  classes,
  columnIndex,
  layoutResource,
  layoutHeight,
  layoutWidth,
  tabRegistry,
  resizeColumnSave,
  ContentProps,
  TabsProps,
  ...rest
}) => {
  const { userResource } = useUserContext();
  const draggingRef = useRef();
  const ref = useRef(null);

  const columnConfiguration = layoutResource.attr('columns').resources().index(columnIndex);
  const columnWidthPercent = parseFloat(columnConfiguration.attr('widthPercent')) / 100;

  let activeTabName = columnConfiguration.attr('activeTabName');

  // Use the first tab if none have been selected, or if we're in a situation where the last opened tab no longer exists
  if (!activeTabName || !tabRegistry[activeTabName]) {
    activeTabName = columnConfiguration.attr('tabs').resources().index(0).attr('name');
  }

  // When you activate a tab then add it to the allow list for rendering
  useHandleVisitTab(activeTabName);

  // Add tab props to content props
  const UpdatedContentProps = ContentProps;
  UpdatedContentProps.openTabs = TabsProps.openTabs;
  UpdatedContentProps.openTab = TabsProps.openTab;
  UpdatedContentProps.setActiveTab = TabsProps.setActiveTab;

  const toggleOnDragging = () => {
    draggingRef.current = true;
  };

  const toggleOffDragging = () => {
    if (draggingRef.current) {
      draggingRef.current = false;
      const currentWindowWidth = parseInt(ref.current.style.width, 10);
      const currentWindowPercent = +((currentWindowWidth / layoutWidth).toFixed(2));
      resizeColumnSave(columnIndex, currentWindowPercent * 100);
    }
  };

  const handleDraggingWidth = (e) => {
    if (draggingRef.current) {
      const deltaX = e.movementX;

      const currentColumnRef = ref.current;
      const currentColumnWidth = parseInt(currentColumnRef.style.width, 10);

      const neighborColumnRef = ref.current.previousSibling;
      const neighborColumnWidth = parseInt(neighborColumnRef.style.width, 10);

      const totalWidth = currentColumnWidth + neighborColumnWidth;
      const updatedCurrentColumnWidth = currentColumnWidth - deltaX;
      const updatedNeighborColumnWidth = totalWidth - updatedCurrentColumnWidth;

      if (updatedCurrentColumnWidth < 0) {
        currentColumnRef.style.width = `${0}px`;
        neighborColumnRef.style.width = `${totalWidth}px`;
      } else if (updatedNeighborColumnWidth < 0) {
        currentColumnRef.style.width = `${totalWidth}px`;
        neighborColumnRef.style.width = `${0}px`;
      } else {
        currentColumnRef.style.width = `${updatedCurrentColumnWidth}px`;
        neighborColumnRef.style.width = `${updatedNeighborColumnWidth}px`;
      }
    }
  };

  useEffect(() => {
    document.addEventListener('mouseup', toggleOffDragging, { capture: true });
    return () => document.removeEventListener('mouseup', toggleOffDragging, { capture: true });
  });

  useEffect(() => {
    document.addEventListener('mousemove', handleDraggingWidth, { capture: true });
    return () => document.removeEventListener('mousemove', handleDraggingWidth, { capture: true });
  });

  return (
    <div
      className={classNames(classes.root, className)}
      style={
        {
          width: `${layoutWidth * columnWidthPercent}px`,
        }}
      ref={ref}
      {...rest}
    >
      {columnIndex > 0 && (
        <IconButton
          className={classes.dragIcon}
          color={pickImplementation(userResource, {
            [fr.relationships.default.name]: 'white',
          })}
          onMouseDown={toggleOnDragging}
          icon={HeightIcon}
          title={null}
        />
      )}
      <div className={classes.tabContainer}>
        <ColumnTabs
          className={classes.tabs}
          {...TabsProps}
          activeTabName={activeTabName}
          columnConfiguration={columnConfiguration}
          columnIndex={columnIndex}
          tabRegistry={tabRegistry}
        />
        <ColumnContent
          className={classes.content}
          {...UpdatedContentProps}
          activeTabName={activeTabName}
          columnConfiguration={columnConfiguration}
          maxHeight={layoutHeight - TABS_HEIGHT}
          tabRegistry={tabRegistry}
        />
      </div>
    </div>
  );
};

Column.defaultProps = {
  className: '',
  ContentProps: {},
  TabsProps: {},
};

Column.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  layoutResource: PropTypes.object.isRequired,
  columnIndex: PropTypes.number.isRequired,
  ContentProps: PropTypes.object,
  TabsProps: PropTypes.object,
  resizeColumnSave: PropTypes.func.isRequired,
};

export default withStyles(styles)(Column);
